import { SerializedError } from "@reduxjs/toolkit";
import { ApiError } from "../model/apiError";
import { Problem } from "../model/problem";

/**
 * Minimal shape shared by every slice state: a status string plus optional
 * error/code. Slices declare a narrower `status` union, which is assignable to
 * `string`, so they can be passed here structurally.
 */
export interface RejectableState {
    status?: string;
    error?: string | Problem;
    code?: number;
}

/** The relevant fields of a `createAsyncThunk` rejected action. */
export interface RejectedThunkAction {
    payload?: ApiError;
    error: SerializedError;
}

/**
 * Canonical rejected-thunk handler, shared by all slices.
 *
 * - Rejections carrying a payload came from `rejectWithValue` (an API error):
 *   a 401 becomes `auth-error` (triggers re-login), anything else `error`.
 * - Rejections without a payload are unexpected (thrown before/without a
 *   response); surface the serialized error message as a plain `error`.
 */
export function applyRejection(state: RejectableState, action: RejectedThunkAction): void {
    if (action.payload) {
        state.error = action.payload.message;
        state.code = action.payload.statusCode;
        state.status = action.payload.statusCode === 401 ? "auth-error" : "error";
    } else {
        state.status = "error";
        state.error = action.error.message;
    }
}
